import { useQuery } from '@apollo/client/react'
import React, { FC, useCallback } from 'react'
import debounce from 'lodash.debounce'
import { useTranslation } from 'react-i18next'
import { StringPaginationType } from '@src/generated/schema'
import { SearchSelectProps } from '@src/components/Elements/SearchSelect/SearchSelect'
import { Pile } from '@src/graphql/Pile'
import { MultiSelect } from '@src/components/Elements/MultiSelect'

export const LogLengthSelect: FC<Pick<SearchSelectProps, 'required'>> = (props) => {
  const { t } = useTranslation()
  const { data, loading, fetchMore, refetch } = useQuery<{ logLengths: StringPaginationType }>(Pile.logLengths, {
    variables: {
      pageSize: 10,
    },
  })
  const { logLengths: { page, strings, hasNext } = { page: 0, strings: [], hasNext: false } } = data || {}

  const handleSearch = useCallback(
    debounce((filterValue: string) => {
      refetch({ filterValue, pageSize: 10 })
    }, 100),
    [],
  )

  const handlePopupScroll = (e: any) => {
    e.persist()
    const scrollTop = e.target.scrollTop as number
    const offsetHeight = e.target.offsetHeight as number

    if (scrollTop + offsetHeight === e.target.scrollHeight && hasNext) {
      const previousPage: number = page || 0

      fetchMore({
        variables: {
          page: previousPage + 1,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const newPage = fetchMoreResult?.logLengths?.page || 0
          const oldPage = prev?.logLengths?.page || 0

          if (!fetchMoreResult || newPage <= oldPage) return prev

          const newEdges = fetchMoreResult.logLengths.strings
          const previousEdges = prev?.logLengths?.strings || []

          return newEdges?.length
            ? {
                logLengths: {
                  page: fetchMoreResult.logLengths.page,
                  pages: fetchMoreResult.logLengths.pages,
                  hasNext: fetchMoreResult.logLengths.hasNext,
                  hasPrevious: fetchMoreResult.logLengths.hasPrevious,
                  __typename: prev.logLengths.__typename,
                  strings: [...previousEdges, ...newEdges],
                },
              }
            : prev
        },
      })
    }
  }

  return (
    <MultiSelect
      {...props}
      label={t('desktop.pages.piles.filter.logLength')}
      options={strings?.map((logLength) => ({
        title: logLength,
        value: logLength,
      }))}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      name='logLength'
      loading={loading}
    />
  )
}
